<template>
  <div class="default-contents">
    <div class="sub-header">
      <div class="display-type" :class="viewMode === 'tile' ? 'list' : 'tile'" @click="changeViewMode">
        {{ viewMode === 'tile' ? 'リスト' : 'タイル' }}表示
      </div>
      <div class="search">
        <input
          type="text"
          placeholder="検索"
          v-model="model.searchText"
          @keydown.enter="$utils.event.strictEnter($event, getContentsFirstPage)"
        />
        <button @click="getContentsFirstPage"><i class="fas fa-search"></i></button>
      </div>
      <portal v-if="isShowTagChipsPopup" to="modal2">
        <chips-popup
          chipType="tag"
          :tags="tags"
          @close="isShowTagChipsPopup = false"
          @chipSelect="selectTag($event)"
        />
      </portal>
    </div>
    <div class="tags-wrapper">
      <ul
        class="tags"
        :style="{height: tags.height + 'px'}"
        ref="tags"
      >
        <li
          v-for="(tag, index) in sortedTagsData"
          :class="{ selected: isTagSelected(tag) }"
          @click="selectTag(tag)"
          :key="index"
        >
          {{tag.name}}
        </li>
      </ul>
      <div
        class="all-open"
        :class="{show: tags.heightAll > 34}"
        @click="isShowTagChipsPopup = true"
      >
        すべてを表示
      </div>
    </div>
    <div class="cards">
      <pagination
        :maxPageIndex="pager.last"
        :currentPageIndex="pager.current"
        @updateCurrentPageIndex="updateCurrentPageIndex"
      />
      <template v-if="!isNoContents">
        <transition>
          <div class="tile" v-if="viewMode === 'tile'">
            <template v-for="content in contents">
              <content-card-tile
                :content="content"
                :id="content.id"
                :key="content.id"
                :isShowInfo="mixinIsShowAsync"
                @createContent="createContent"
                @htmlPreview="htmlPreview"
                @captureError="captureError"
              />
            </template>
          </div>
        </transition>
        <transition>
          <div class="list" v-if="viewMode === 'list'">
            <template v-for="content in contents">
              <content-card-list
                :content="content"
                :id="content.id"
                :key="content.id"
                :isShowInfo="mixinIsShowAsync"
                @createContent="createContent"
                @htmlPreview="htmlPreview"
                @captureError="captureError"
              />
            </template>
          </div>
        </transition>
      </template>
      <template v-else>
        <div class="no-contents">
          <p>テンプレートがありません</p>
        </div>
      </template>
      <pagination
        :maxPageIndex="pager.last"
        :currentPageIndex="pager.current"
        @updateCurrentPageIndex="updateCurrentPageIndex"
      />
    </div>
  </div>
</template>

<script>
import ContentCardTile from '@/components/Content/Manager/ContentCardTile'
import ContentCardList from '@/components/Content/Manager/ContentCardList'
import Pagination from '@/components/Util/Pagination'
import ChipsPopup from '@/components/Contents/ChipsPopup'
import AsyncShowBase from '@/mixins/AsyncShowBase'

const ONE_LINE_HEIGHT = 24

export default {
  components: {
    ContentCardTile,
    ContentCardList,
    Pagination,
    ChipsPopup
  },
  props: [
    'contentType',
  ],
  mixins: [
    AsyncShowBase,
  ],
  data () {
    return {
      isNoContents: false,
      isShowTagChipsPopup: false,
      tags: {
        data: [],
        selects: 'all', // 'all' or [('unset'), (1)]
        height: ONE_LINE_HEIGHT,
        heightAll: 0,
      },
      model: {
        searchText: '',
      },
      contents: [],
      viewMode: 'tile',
      isCreating: false,
      pager: {
        current: 1,
        last: 1,
      },
    }
  },
  created () {
    this.initComponent()
  },
  mounted () {
    this.mixinAsyncShow('contents')
  },
  computed: {
    isTagSelected () {
      return function (tag) {
        if (tag.id === 'all') {
          return this.tags.selects === 'all'
        } else {
          return Array.isArray(this.tags.selects) && this.tags.selects.some(select => tag.id === select)
        }
      }
    },
    sortedTagsData () {
      // タグ名が 未設定 > 選択されている > 選択されていない の順番でソートしています
      const [ unsetTagsData, ...notUnsetTagsData ] = this.tags.data
      const selectedTags = notUnsetTagsData.filter(tag => this.isTagSelected(tag))
      const notSelectedTags = notUnsetTagsData.filter(tag => !this.isTagSelected(tag))
      return unsetTagsData == null ? [
        ...selectedTags,
        ...notSelectedTags,
      ] : [
        unsetTagsData,
        ...selectedTags,
        ...notSelectedTags,
      ]
    },
  },
  methods: {
    initComponent () {
      this.getDefaultDisplay().then(() => {
        this.getContentsFirstPage()
        this.getTags()
      })
    },
    updateCurrentPageIndex (index) {
      if (index > this.pager.total || index < 1) return
      this.pager.current = index
      this.getContents()
    },
    getContents () {
      const urlSP = new URLSearchParams()
      urlSP.append('searchText', this.model.searchText)
      urlSP.append('page', this.pager.current)
      if (this.tags.selects === 'all') {
        urlSP.append('tags', 'all')
      } else {
        this.tags.selects.forEach(select => {
          urlSP.append('tags[]', select)
        })
      }
      this.$http({
        url: `/default_content/${this.contentType}?${urlSP.toString()}`,
        type: 'GET',
      }).then(json => {
        if (json && json.status === 200) {
          this.contents = json.data.contents.map(content => {
            const tags = content.tags.map(tag => {
              return {
                ...tag,
                name: this.$utils.string.b64Decode(tag.name)
              }
            })
            return {
              ...content,
              tags,
              captureUrl: decodeURIComponent(content.captureUrl)
            }
          })
          this.pager.last = json.data.lastPage
          this.isNoContents = (this.contents.length === 0)
        }
      })
    },
    getContentsFirstPage () {
      this.pager.current = 1
      this.getContents()
    },
    changeViewMode () {
      this.viewMode = this.viewMode === 'tile' ? 'list' : 'tile'

      // タイル or リスト 表示設定を保存
      const formData = new FormData()
      formData.append('contentNewViewMode', this.viewMode)
      this.$http({
        url: '/user/mphe_settings/change_default_display',
        type: 'POST',
        params: formData,
      })
    },
    getDefaultDisplay () {
      return this.$http({
        url: `/user/mphe_settings/default_display_content_new/${this.contentType}`,
        type: 'GET',
      }).then(json => {
        if (json && json.status === 200) {
          const { viewMode, grepTags } = json.data.display
          this.viewMode = viewMode
          this.tags.selects = grepTags.length ? grepTags : 'all'
        }
        return json
      })
    },
    createContent (content) {
      // json読み込み
      if (this.isCreating) return
      this.isCreating = true
      const formData = new FormData()
      formData.append('contentId', content.id)
      formData.append('type', this.contentType)
      this.$http({
        url: '/content/new',
        type: 'POST',
        params: formData
      }).then(json => {
        if (json && json.status === 200) {
          this.$router.push({ path: `/${this.$route.params.mpDomain}/${this.$route.params.siteId}/content/edit/${json.data.contentId}` })
        }
        this.isCreating = false
      })
    },
    async htmlPreview (content) {
      const { href } = this.$router.resolve(`/${this.$route.params.mpDomain}/${this.$route.params.siteId}/content_new/preview/${this.contentType}/${content.id}`)
      window.open(href, '_blank')
    },
    captureError (event) {
      event.target.src = '../../../../../static/loading.png'
    },
    getTags () {
      return this.$http({
        url: `/default_content/tag?type=${this.contentType}`,
        type: 'GET',
      }).then(json => {
        if (json && json.status === 200) {
          this.tags.data = [
            {
              id: 'unset',
              name: '未設定',
            },
            ...json.data.tags.map(tag => {
              return {
                ...tag,
                name: this.$utils.string.b64Decode(tag.name)
              }
            })
          ]
          this.$nextTick(() => {
            if (!this.$refs.tags) return
            this.tags.heightAll = this.$refs.tags.scrollHeight
          })
        }
      })
    },
    selectTag (tag) {
      if (tag.id === 'all') {
        this.tags.selects = 'all'
      } else {
        if (this.tags.selects === 'all') {
          this.tags.selects = [tag.id]
        } else if (this.tags.selects.some(select => select === tag.id)) {
          this.tags.selects = this.tags.selects.filter(select => select !== tag.id)
        } else {
          this.tags.selects = [ ...this.tags.selects, tag.id ]
        }
      }
      this.getContentsFirstPage()
    }
  },
  beforeDestroy () {
    this.$store.commit('clearStateToLeaveContentsList')
  },
  watch: {
    contentType () {
      // レイアウト・デザイン等切替えた時に再読み込みさせる
      this.tags.height = ONE_LINE_HEIGHT
      this.tags.heightAll = 0
      this.tags.selects = 'all'
      this.initComponent()
    }
  }
}
</script>

<style lang="scss" scoped>
.default-contents {
  // タグ機能
  .tags-wrapper {
    display:-webkit-box;
    display:flex;
    margin: 0 0 50px 20px;
    .tags {
      margin: 0;
      display:-webkit-box;
      display: flex;
      flex-wrap: wrap;
      transition: all 0.3s ease 0s;
      overflow: hidden;
      &::before {
        font-family: "Font Awesome 5 Free";
        content: '\f02b';
        font-weight: 900;
      }
      li {
        height: 24px;
        line-height: 24px;
        display: inline-block;
        padding: 0 10px;
        cursor: pointer;
        margin: 0 10px 10px 0;
        background: #fff;
        border-radius: 12px;
        transition: all 0.3s ease 0s;
        &:first-of-type {
          margin-left: 10px;
        }
        &:hover {
          background: rgba($primaryHoverColor, 0.4);
        }
        &.selected {
          color: #fff;
          background: $primaryActiveColor;
          &:hover {
            background: $primaryHoverColor;
          }
        }
      }
    }
    // すべてを表示 ボタン
    .all-open {
      height: 24px;
      font-size: 12px;
      line-height: 24px;
      display: inline-block;
      padding: 0 10px;
      flex-shrink: 0;
      border-radius: 12px;
      transition: all 0.3s ease 0s;
      visibility: hidden;
      cursor: pointer;
      &::before {
        font-family: "Font Awesome 5 Free";
        content: '\f022';
        font-weight: 900;
      }
      &.show {
        visibility: visible;
      }
      &:hover, &:active {
        background: #e4e4e4;
        transition: all 0.3s ease 0s;
      }
    }
  }
  // 検索・表示型切り替えボタン
  .sub-header {
    position: absolute;
    top: 6px;
    right: 0;
    text-align: right;
    // 画面幅が狭い時、メーリングのメニューが押せなくなるため
    pointer-events: none;
    > div {
      // 再度クリック可能になるようにしている
      pointer-events: auto;
      &:not(.search) {
        display: inline-block;
        margin-bottom: 10px;
      }
    }
    .search {
      width: 400px;
      display: block;
      position: relative;
      &>input {
        width: 100%;
        height: 35px;
        padding:0 10px;
        border-radius:2px;
        outline:0;
        background: #E4E4E4;
        position: absolute;
        left: -20px;
        top: 0;
      }
      &>button {
        width: 50px;
        height: 35px;
        border-radius:0 2px 2px 0;
        outline:0;
        position: absolute;
        right: 0;
        top: 0;
        background: none;
        transition: all 0.3s ease 0s;
        cursor: pointer;
        &:hover, &:active {
          background: #c6c6c6;
          transition: all 0.3s ease 0s;
        }
      }
    }
    .display-type {
      font-size: 12px;
      padding: 0 15px;
      box-sizing: border-box;
      width: 106px;
      white-space: nowrap;
      height: 35px;
      line-height: 35px;
      border-radius: 2px;
      transition: all 0.3s ease 0s;
      cursor: pointer;
      &:hover, &:active {
        background: #e4e4e4;
        transition: all 0.3s ease 0s;
      }
      &::before {
        font-family: "Font Awesome 5 Free";
        font-weight: 900;
      }
      &.tile {
        &::before {
          content: '\f00a';
          padding-right: 4px;
        }
      }
      &.list {
        &::before {
          content: '\f03a';
        }
      }
    }
  }
  .no-contents {
    width: 100%;
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
    p {
      font-size: 14px;
    }
  }
  // タイル表示のときだけ横並び
  .tile {
    display:-webkit-box;
    display:flex;
    flex-wrap: wrap;
  }
  // 表示型切り替えボタンクリック時のアニメーション
    .v-enter-active {
    transition: opacity .3s ease-in-out;
  }
  .v-enter, .v-leave, .v-leave-to {
    opacity: 0;
  }
  .v-enter-to{
    opacity: 1;
  }
}
</style>
